import axios from "axios";

const BACKEND_URL = (typeof import.meta !== "undefined" && import.meta.env?.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || "";
const BASE = BACKEND_URL?.replace(/\/$/, "");
const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API });

export function setAuthToken(token) {
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete api.defaults.headers.common["Authorization"];
}

// Auth
export async function authSignup({ name, email, password, role = "customer", company_id }) {
  const { data } = await api.post("/auth/signup", { name, email, password, role, company_id });
  return data; // {access_token}
}
export async function authLogin({ email, password }) {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
}
export async function authMe() { const { data } = await api.get("/auth/me"); return data; }
export async function patchMe(payload) { const { data } = await api.patch("/users/me", payload); return data; }

// Plans, vendors
export async function fetchPlans() { const { data } = await api.get("/plans"); return data; }
export async function fetchVendors(cityLabel) { const params = cityLabel ? { city: cityLabel } : undefined; const { data } = await api.get("/vendors", { params }); return data; }
export async function fetchVendorDetail(id) { const { data } = await api.get(`/vendors/${id}`); return data; }
export async function fetchVendorMenu(id) { const { data } = await api.get(`/vendors/${id}/menu`); return data; }
export async function fetchPromotions(vendorId) { const { data } = await api.get(`/vendors/${vendorId}/promotions`); return data; }
export async function fetchInviteVendor(token){ 
  // Check if diagnostics mode is enabled
  const isDebug = localStorage.getItem('prepmate_debug') === '1' || 
                  new URLSearchParams(window.location.search).get('debug') === '1';
  
  const headers = isDebug ? { 'X-Prepmate-Debug': '1' } : {};
  const { data } = await api.get(`/vendors/invite/${token}`, { headers }); 
  return data; 
}

// Favorites
export async function favVendor(vendorId) { const { data } = await api.post(`/users/me/favorites/vendors/${vendorId}`); return data; }
export async function unfavVendor(vendorId) { const { data } = await api.delete(`/users/me/favorites/vendors/${vendorId}`); return data; }
export async function favMeal(mealId) { const { data } = await api.post(`/users/me/favorites/meals/${mealId}`); return data; }
export async function unfavMeal(mealId) { const { data } = await api.delete(`/users/me/favorites/meals/${mealId}`); return data; }

// Menu management (vendor)
export async function createMenuItem(vendorId, payload) { const { data } = await api.post(`/vendors/${vendorId}/menu`, payload); return data; }
export async function updateMenuItem(vendorId, itemId, payload) { const { data } = await api.put(`/vendors/${vendorId}/menu/${itemId}`, payload); return data; }
export async function patchItemAvailability(vendorId, itemId, available) { const { data } = await api.patch(`/vendors/${vendorId}/menu/${itemId}/availability`, { available }); return data; }
export async function deleteMenuItem(vendorId, itemId) { const { data } = await api.delete(`/vendors/${vendorId}/menu/${itemId}`); return data; }

// Orders
export async function listVendorOrders(vendorId) { const { data } = await api.get(`/vendors/${vendorId}/orders`); return data; }
export async function updateOrderStatus(orderId, status) { const { data } = await api.patch(`/orders/${orderId}/status`, { status }); return data; }
export async function createOrder(payload) { const { data } = await api.post("/orders", payload); return data; }

// Subscriptions
export async function fetchSubscriptions(vendorId, status) { const params = status ? { status } : undefined; const { data } = await api.get(`/vendors/${vendorId}/subscriptions`, { params }); return data; }
export async function createSubscription(vendorId, payload) { const { data } = await api.post(`/vendors/${vendorId}/subscriptions`, payload); return data; }
export async function updateSubscription(vendorId, subId, payload) { const { data } = await api.patch(`/vendors/${vendorId}/subscriptions/${subId}`, payload); return data; }
export async function sendReminder(vendorId, subId) { const { data } = await api.post(`/vendors/${vendorId}/subscriptions/${subId}/remind`); return data; }
export async function fetchCustomers(vendorId) { const { data } = await api.get(`/vendors/${vendorId}/customers`); return data; }
export async function mySubscriptions() { const { data } = await api.get(`/users/me/subscriptions`); return data; }
export async function patchVendorSettings(vendorId, payload) { const { data } = await api.patch(`/vendors/${vendorId}/settings`, payload); return data; }

// Checkout
export async function getCheckoutQuote(payload) { const { data } = await api.post(`/checkout/quote`, payload); return data; }

// Stripe Payment
export async function createPaymentIntent(payload) { const { data } = await api.post(`/checkout/create-payment-intent`, payload); return data; }
export async function getWeeklyOrderStatus(orderId) { const { data } = await api.get(`/checkout/order/${orderId}`); return data; }
export async function confirmPaymentManual(orderId) { const { data } = await api.post(`/checkout/confirm-payment/${orderId}`); return data; }

// Admin
export async function resetDemoData() { const { data } = await api.post(`/admin/reset-demo-data`); return data; }

// Vendor applications
export async function submitVendorApplication(payload) { const { data } = await api.post(`/vendor-applications`, payload); return data; }

// Superadmin
export async function saGetStats() { const { data } = await api.get(`/superadmin/stats`); return data; }
export async function saGetVendors() { const { data } = await api.get(`/superadmin/vendors`); return data; }
export async function saCreateVendor(payload) { const { data } = await api.post(`/superadmin/vendors`, payload); return data; }
export async function saUpdateVendor(id, payload) { const { data } = await api.patch(`/superadmin/vendors/${id}`, payload); return data; }
export async function saToggleVendor(id) { const { data } = await api.patch(`/superadmin/vendors/${id}/toggle`); return data; }
export async function saGetCustomers() { const { data } = await api.get(`/superadmin/customers`); return data; }
export async function saDeactivateCustomer(id) { const { data } = await api.patch(`/superadmin/customers/${id}/deactivate`); return data; }
export async function saGetApplications() { const { data } = await api.get(`/superadmin/applications`); return data; }
export async function saUpdateApplicationStatus(id, status) { const { data } = await api.patch(`/superadmin/applications/${id}/status`, { status }); return data; }
export async function saGetOrders() { const { data } = await api.get(`/superadmin/orders`); return data; }